import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Alert,
  InputAdornment,
  IconButton,
  LinearProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  Lock,
  CheckCircle,
  Cancel,
  VpnKey,
} from '@mui/icons-material';
import axios from 'axios';

const ChangePassword = () => {
  const navigate = useNavigate();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  // Password strength indicators
  const [passwordStrength, setPasswordStrength] = useState({
    length: false,
    uppercase: false,
    lowercase: false,
    number: false,
    special: false,
  });

  useEffect(() => {
    // Check if user needs to change password
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/login');
      return;
    }

    try {
      const decoded = JSON.parse(atob(token.split('.')[1]));

      // If user doesn't have mustChangePassword flag, redirect to dashboard
      if (!decoded.mustChangePassword) {
        navigate('/');
      }
    } catch (error) {
      console.error('Token decode error:', error);
      navigate('/login');
    }
  }, [navigate]);

  // Check password strength
  useEffect(() => {
    setPasswordStrength({
      length: newPassword.length >= 8,
      uppercase: /[A-Z]/.test(newPassword),
      lowercase: /[a-z]/.test(newPassword),
      number: /\d/.test(newPassword),
      special: /[@$!%*?&]/.test(newPassword),
    });
  }, [newPassword]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validation
    if (!currentPassword || !newPassword || !confirmPassword) {
      setError('All fields are required');
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('New passwords do not match');
      return;
    }

    if (newPassword.length < 8) {
      setError('Password must be at least 8 characters long');
      return;
    }

    // Check if all strength requirements are met
    const allRequirementsMet = Object.values(passwordStrength).every(val => val);
    if (!allRequirementsMet) {
      setError('Password does not meet all requirements');
      return;
    }

    if (currentPassword === newPassword) {
      setError('New password must be different from current password');
      return;
    }

    setLoading(true);

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        '/api/auth/change-owner-password',
        {
          currentPassword,
          newPassword,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.data.success) {
        // Update token with new one (without mustChangePassword flag)
        localStorage.setItem('token', response.data.data.token);

        setSuccess(true);
        setTimeout(() => {
          navigate('/');
        }, 2000);
      }
    } catch (err) {
      console.error('Password change error:', err);
      setError(
        err.response?.data?.message ||
        'Failed to change password. Please try again.'
      );
    } finally {
      setLoading(false);
    }
  };

  const strengthIndicator = (met) => (
    <ListItemIcon>
      {met ? (
        <CheckCircle sx={{ color: 'success.main' }} />
      ) : (
        <Cancel sx={{ color: 'error.main' }} />
      )}
    </ListItemIcon>
  );

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        py: 4,
      }}
    >
      <Container maxWidth="sm">
        <Paper
          elevation={24}
          sx={{
            p: 4,
            borderRadius: 3,
          }}
        >
          {/* Header */}
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <VpnKey sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
            <Typography variant="h4" gutterBottom fontWeight="bold">
              Change Password
            </Typography>
            <Typography variant="body2" color="text.secondary">
              You must change your temporary password before continuing
            </Typography>
          </Box>

          {/* Success Message */}
          {success && (
            <Alert severity="success" sx={{ mb: 3 }}>
              <Typography variant="body1" fontWeight="medium">
                Password changed successfully! Redirecting to dashboard...
              </Typography>
            </Alert>
          )}

          {/* Error Message */}
          {error && (
            <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
              {error}
            </Alert>
          )}

          {/* Form */}
          {!success && (
            <Box component="form" onSubmit={handleSubmit}>
              {/* Current Password */}
              <TextField
                fullWidth
                label="Current Password"
                type={showCurrentPassword ? 'text' : 'password'}
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                margin="normal"
                required
                autoFocus
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Lock color="action" />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                        edge="end"
                      >
                        {showCurrentPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              {/* New Password */}
              <TextField
                fullWidth
                label="New Password"
                type={showNewPassword ? 'text' : 'password'}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                margin="normal"
                required
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Lock color="action" />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowNewPassword(!showNewPassword)}
                        edge="end"
                      >
                        {showNewPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              {/* Confirm New Password */}
              <TextField
                fullWidth
                label="Confirm New Password"
                type={showConfirmPassword ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                margin="normal"
                required
                error={confirmPassword && newPassword !== confirmPassword}
                helperText={
                  confirmPassword && newPassword !== confirmPassword
                    ? 'Passwords do not match'
                    : ''
                }
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Lock color="action" />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        edge="end"
                      >
                        {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              {/* Password Requirements */}
              {newPassword && (
                <Box sx={{ mt: 3, mb: 2 }}>
                  <Typography variant="subtitle2" gutterBottom fontWeight="medium">
                    Password Requirements:
                  </Typography>
                  <List dense>
                    <ListItem disableGutters>
                      {strengthIndicator(passwordStrength.length)}
                      <ListItemText
                        primary="At least 8 characters"
                        primaryTypographyProps={{
                          variant: 'body2',
                          color: passwordStrength.length ? 'success.main' : 'text.secondary',
                        }}
                      />
                    </ListItem>
                    <ListItem disableGutters>
                      {strengthIndicator(passwordStrength.uppercase)}
                      <ListItemText
                        primary="One uppercase letter (A-Z)"
                        primaryTypographyProps={{
                          variant: 'body2',
                          color: passwordStrength.uppercase ? 'success.main' : 'text.secondary',
                        }}
                      />
                    </ListItem>
                    <ListItem disableGutters>
                      {strengthIndicator(passwordStrength.lowercase)}
                      <ListItemText
                        primary="One lowercase letter (a-z)"
                        primaryTypographyProps={{
                          variant: 'body2',
                          color: passwordStrength.lowercase ? 'success.main' : 'text.secondary',
                        }}
                      />
                    </ListItem>
                    <ListItem disableGutters>
                      {strengthIndicator(passwordStrength.number)}
                      <ListItemText
                        primary="One number (0-9)"
                        primaryTypographyProps={{
                          variant: 'body2',
                          color: passwordStrength.number ? 'success.main' : 'text.secondary',
                        }}
                      />
                    </ListItem>
                    <ListItem disableGutters>
                      {strengthIndicator(passwordStrength.special)}
                      <ListItemText
                        primary="One special character (@$!%*?&)"
                        primaryTypographyProps={{
                          variant: 'body2',
                          color: passwordStrength.special ? 'success.main' : 'text.secondary',
                        }}
                      />
                    </ListItem>
                  </List>
                </Box>
              )}

              {/* Submit Button */}
              <Button
                type="submit"
                fullWidth
                variant="contained"
                size="large"
                disabled={loading}
                sx={{
                  mt: 3,
                  py: 1.5,
                  fontWeight: 'bold',
                  textTransform: 'none',
                  fontSize: '1rem',
                }}
              >
                {loading ? 'Changing Password...' : 'Change Password'}
              </Button>

              {/* Loading Progress */}
              {loading && <LinearProgress sx={{ mt: 2 }} />}
            </Box>
          )}

          {/* Security Note */}
          <Box sx={{ mt: 4, textAlign: 'center' }}>
            <Typography variant="caption" color="text.secondary">
              🔒 Your password is encrypted and secure
            </Typography>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

export default ChangePassword;
